import { useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { Outlet, createFileRoute } from '@tanstack/react-router';

import isEmpty from 'lodash/isEmpty';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import {
    useCreateCareInstructionsMutation,
    useGetCareInstructionsQuery,
} from 'src/store/sageHomeApi';
import { setCareInstructions } from 'src/store/slices/careInstructionsSlice';
import { useAppDispatch } from 'src/store/store';

export const Route = createFileRoute('/(app)/care')({
    component: CareLayout,
});

function CareLayout() {
    const dispatch = useAppDispatch();
    const { careRecipient, currentUser } = useTeamMemberData();

    const careRecipientRid = careRecipient?.teamMemberRid || '';

    const {
        data: careInstructionsData,
        isLoading: isLoadingCareInstructions,
        isUninitialized,
        isError: isGetCareInstructionsError,
    } = useGetCareInstructionsQuery({ careRecipientRid }, { skip: !careRecipientRid });

    const [
        createCareInstructions,
        { isLoading: isCreatingCareInstructions, isError: isCreateCareInstructionsError },
    ] = useCreateCareInstructionsMutation();

    // Set care instructions in state
    useEffect(() => {
        if (careInstructionsData?.careInstructions) {
            dispatch(setCareInstructions(careInstructionsData.careInstructions));
        }
    }, [careInstructionsData?.careInstructions, dispatch]);

    // Handle creating care instructions if none exist
    useEffect(() => {
        if (
            isUninitialized ||
            isLoadingCareInstructions ||
            isCreatingCareInstructions ||
            isCreateCareInstructionsError ||
            isGetCareInstructionsError ||
            !careRecipientRid ||
            !currentUser?.isTeamAdmin
        ) {
            return;
        }

        if (isEmpty(careInstructionsData?.careInstructions)) {
            createCareInstructions({ careRecipientRid }).catch((error) => {
                datadogLogs.logger.error(
                    'Error creating care instructions',
                    { careRecipientRid },
                    error
                );
            });
        }
    }, [
        careInstructionsData?.careInstructions,
        careRecipientRid,
        createCareInstructions,
        isCreateCareInstructionsError,
        isCreatingCareInstructions,
        isGetCareInstructionsError,
        isLoadingCareInstructions,
        isUninitialized,
        currentUser,
    ]);

    return <Outlet />;
}
